/**
 * 확장된 라우터 back 동작을 제공하는 커스텀 훅
 *
 * 기존 Next.js의 router.back() 메서드는 성공/실패 여부를 반환하지 않는 문제가 있습니다.
 * 특히 Safari 브라우저에서는 첫 페이지에서도 history.length가 2인 경우 동작하지 않을때가 있습니다. (새창 열기 -> 다른곳 이동 -> 뒤로가기 -> 뒤로가기(미동작))
 *
 * router.back()의 성공 여부를 Promise 형태로 반환하여 이러한 문제를 해결합니다.
 * 성공 시 { success: true, url: '이동한 URL' }을 반환하고,
 * 실패 시 { success: false, reason: '실패 이유' }를 반환합니다.
 */
import { useCallback } from 'react'

import { useRouter } from 'next/router'

export function useRouterBack() {
  const router = useRouter()

  const goBack = useCallback(
    (option?: {
      timeout?: number
    }): Promise<{
      success: boolean
      url?: string
      reason?: string
    }> => {
      return new Promise((resolve, reject) => {
        const handleRouteChangeComplete = (url: string): void => {
          router.events.off('routeChangeComplete', handleRouteChangeComplete)
          router.events.off('routeChangeError', handleRouteChangeError)
          clearTimeout(timeoutId)
          resolve({ success: true, url })
        }

        const handleRouteChangeError = (err: Error): void => {
          router.events.off('routeChangeComplete', handleRouteChangeComplete)
          router.events.off('routeChangeError', handleRouteChangeError)
          clearTimeout(timeoutId)
          reject(err)
        }

        router.events.on('routeChangeComplete', handleRouteChangeComplete)
        router.events.on('routeChangeError', handleRouteChangeError)

        router.back()

        // 타임아웃 사간안에 뒤로가기 안되면 뒤로가기 실패로 처리
        const timeoutId: NodeJS.Timeout = setTimeout(() => {
          router.events.off('routeChangeComplete', handleRouteChangeComplete)
          router.events.off('routeChangeError', handleRouteChangeError)
          resolve({
            success: false,
            reason: `뒤로가기 실패, 타임아웃 ${option?.timeout || 500}ms`,
          })
        }, option?.timeout || 500)
      })
    },
    [router],
  )

  return { goBack }
}
